import type { FunctionComponent, VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { PlanSelector } from '../settings/billing/PlanSelector';
import type { FeatureDescription, FossaFeature } from '../settings/billing/features/consts';
import { getFeatureDetails } from '../settings/billing/features/consts';
import type { FossaPlanNameWithEnterprise } from '../../../shared/subscriptions';
import { getSubscription, isEnterpriseTier, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../shared/subscriptions';
import Flex from '../generic/Flex';
import Text from '../generic/Text';
import { RouterLink } from '../generic/Link';

export type MessageOptions = {
  focusFeature?: FossaFeature;
  tier?: FossaPlanNameWithEnterprise;
};

type ModalPlanSelectorProps = {
  onHide: () => void;
  isShowing: boolean;
  messageOptions?: MessageOptions;
};

type TierOnlyHeaderProps = {
  tier: FossaPlanNameWithEnterprise;
};

type FeatureAndTierHeaderProps = {
  featureDetails: FeatureDescription;
  tier?: FossaPlanNameWithEnterprise;
};

type PlanSelectorHeaderProps = {
  featureDetails: FeatureDescription | null;
  messageOptions?: MessageOptions;
};

const ENTERPRISE_TIER_OBJECT = {
  label: 'Enterprise',
  description: 'Compliance',
};

const TierOnlyHeader: VoidFunctionComponent<TierOnlyHeaderProps> = ({ tier }) => {
  const { label, description } = tier === 'Enterprise' ? ENTERPRISE_TIER_OBJECT : getSubscription(tier);

  return (
    <div className="restricted-feature-header">
      <Text.H1>
        Available with a{(tier === 'Enterprise' || isEnterpriseTier({ plan: tier })) && 'n'}
        <span className="title-emphasis">
          {label} {description}
        </span>
        Plan
      </Text.H1>
    </div>
  );
};

const FeatureAndTierHeader: VoidFunctionComponent<
  FeatureAndTierHeaderProps
> = ({ tier = PREMIUM_COMPLIANCE_PLAN_NAME, featureDetails }) => {
  const { label, description } = tier === 'Enterprise' ? ENTERPRISE_TIER_OBJECT : getSubscription(tier);
  return (
    <div className="restricted-feature-header">
      <Text.H1>
        <span className="title-emphasis">{featureDetails.name}</span>
        available with a{(tier === 'Enterprise' || isEnterpriseTier(tier)) && 'n'}
        <span className="title-emphasis">
          {label} {description}
        </span>
        Plan
      </Text.H1>
    </div>
  );
};

const PlanSelectorHeader: VoidFunctionComponent<PlanSelectorHeaderProps> = ({
  featureDetails,
  messageOptions,
}) => {
  return featureDetails ? (
    <FeatureAndTierHeader
      tier={messageOptions?.tier || PREMIUM_COMPLIANCE_PLAN_NAME}
      featureDetails={featureDetails}
    />
  ) : (
    <TierOnlyHeader
      tier={messageOptions?.tier || PREMIUM_COMPLIANCE_PLAN_NAME}
    />
  );
};

const ModalPlanSelector: FunctionComponent<ModalPlanSelectorProps> = ({
  onHide,
  isShowing,
  messageOptions,
}) => {
  const featureDetails = getFeatureDetails(messageOptions?.focusFeature);
  return (
    <Modal
      show={isShowing}
      onHide={onHide}
      dialogClassName="plan-selector-modal-container"
    >
      <Modal.Header closeButton>
        <PlanSelectorHeader
          featureDetails={featureDetails}
          messageOptions={messageOptions}
        />
      </Modal.Header>
      <Modal.Body>
        <PlanSelector />
      </Modal.Body>
      <Modal.Footer>
        <Flex grow="inherit">
          <RouterLink to="/account/settings/billing" onClick={onHide}>
            Full plan details
          </RouterLink>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalPlanSelector;
